/**
 * Formats an ISO date string or DD/MM/YYYY string into a localized readable format.
 * Handles both `2026-03-05T09:15:00.000Z` and `05/03/2026` inputs.
 *
 * @param dateStr - Date string in ISO or DD/MM/YYYY format
 * @param locale - Locale string for formatting (default: "vi-VN")
 * @returns Formatted date string e.g. "05/03/2026" or "05 Tháng 3, 2026"
 */
export const formatDate = (dateStr: string, locale = "vi-VN"): string => {
  if (!dateStr) return "";

  let date: Date;

  // Check if it's already DD/MM/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    return dateStr;
  }

  // Try parsing as ISO string
  date = new Date(dateStr);

  if (isNaN(date.getTime())) {
    return dateStr; // Return raw string if unparseable
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Parses a date string into a Unix timestamp for sorting.
 * Handles both ISO strings and DD/MM/YYYY format.
 *
 * @param dateStr - Date string in ISO or DD/MM/YYYY format
 * @returns Timestamp in milliseconds
 */
export const parseDateToTimestamp = (dateStr: string): number => {
  if (!dateStr) return 0;

  // Check if DD/MM/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split("/").map(Number);
    return new Date(year, month - 1, day).getTime();
  }

  // Otherwise treat as ISO
  const ts = new Date(dateStr).getTime();
  return isNaN(ts) ? 0 : ts;
};
/**
 * Formats an ISO date string into a localized date and time string.
 * e.g. "05:42, 18/03/2026"
 *
 * @param dateStr - ISO date string
 * @param locale - Locale string
 * @returns Formatted date-time string
 */
export const formatDateTime = (dateStr: string, locale = "vi-VN"): string => {
  if (!dateStr) return "";

  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${hours}:${minutes}, ${day}/${month}/${year}`;
};
